import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  AlertTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Avatar,
  alpha,
  useTheme,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Security,
  CloudUpload,
  Delete,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Link as LinkIcon,
  Visibility,
  VisibilityOff,
  Business,
  Info,
  AccountBalance,
  VpnKey,
  Save,
  ContentCopy,
  Settings,
  Api,
  ArrowForward,
  Webhook,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { certificatesApi, api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentCompany } from '@/store/slices/authSlice';

interface Certificate {
  id: string;
  alias: string;
  description?: string;
  serialNumber: string;
  issuer: string;
  subject: string;
  thumbprint: string;
  validFrom: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'revoked';
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
}

interface IntegrationStatus {
  hasCertificate: boolean;
  certificateCount: number;
  activeCertificate?: {
    id: string;
    alias: string;
    subject: string;
    expiresAt: string;
    daysUntilExpiry: number;
  };
  environment: string;
  clientIdConfigured: boolean;
}

interface SicoobConfig {
  environment: 'sandbox' | 'production';
  clientId: string;
  cooperativa: string;
  contaCorrente: string;
  contaPoupanca?: string;
  numeroCliente: string;
  descricaoApp: string;
  authUrl: string;
  apiPixUrl: string;
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SicoobSettingsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const company = useAppSelector(selectCurrentCompany);

  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState<{ success: boolean; message: string } | null>(null);

  // Configurações Sicoob
  const [config, setConfig] = useState<SicoobConfig>({
    environment: 'sandbox',
    clientId: '',
    cooperativa: '',
    contaCorrente: '',
    contaPoupanca: '',
    numeroCliente: '',
    descricaoApp: '',
    authUrl: 'https://auth.sicoob.com.br/auth/realms/cooperado/protocol/openid-connect/token',
    apiPixUrl: 'https://api.sicoob.com.br/pix/api/v2',
  });

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    alias: '',
    description: '',
    password: '',
    file: null as File | null,
  });
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Resetar estados quando a empresa mudar
    setConfig({
      environment: 'sandbox',
      clientId: '',
      cooperativa: '',
      contaCorrente: '',
      contaPoupanca: '',
      numeroCliente: '',
      descricaoApp: '',
      authUrl: 'https://auth.sicoob.com.br/auth/realms/cooperado/protocol/openid-connect/token',
      apiPixUrl: 'https://api.sicoob.com.br/pix/api/v2',
    });
    setStatus(null);
    setCertificates([]);
    setConnectionResult(null);

    if (company?.id) {
      loadData();
    }
  }, [company?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, certsRes] = await Promise.all([
        certificatesApi.getStatus(),
        certificatesApi.list(),
      ]);

      if (statusRes.success) {
        setStatus(statusRes.data);
      }
      if (certsRes.success) {
        setCertificates(certsRes.data);
      }

      // Carregar configurações salvas da empresa
      try {
        const configRes = await api.get('/companies/sicoob-config');
        console.log('Sicoob config response:', configRes);
        if (configRes?.success && configRes?.data) {
          const data = configRes.data;
          setConfig(prev => ({
            ...prev,
            environment: data.environment || 'sandbox',
            clientId: data.clientId || '',
            cooperativa: data.cooperativa || '',
            contaCorrente: data.contaCorrente || '',
            contaPoupanca: data.contaPoupanca || '',
            numeroCliente: data.numeroCliente || '',
            descricaoApp: data.descricaoApp || '',
            authUrl: data.authUrl || prev.authUrl,
            apiPixUrl: data.apiPixUrl || prev.apiPixUrl,
          }));
        }
      } catch (e) {
        // Config não existe ainda, usar defaults
        console.log('Sicoob config not found, using defaults');
      }

    } catch (error: any) {
      // Usar mock em desenvolvimento
      setStatus({
        hasCertificate: false,
        certificateCount: 0,
        environment: 'sandbox',
        clientIdConfigured: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.clientId || !config.cooperativa || !config.contaCorrente) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/companies/sicoob-config', config);
      if (response?.success) {
        toast.success('Configurações salvas com sucesso!');
      } else {
        toast.error('Erro ao salvar configurações');
      }
    } catch (error: any) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);

    try {
      const response = await certificatesApi.testConnection();
      setConnectionResult({
        success: response.success && response.data?.success,
        message: response.data?.message || 'Teste concluído',
      });

      if (response.success && response.data?.success) {
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error(response.data?.message || 'Falha na conexão');
      }
    } catch (error: any) {
      setConnectionResult({
        success: false,
        message: error.error?.message || 'Erro ao testar conexão',
      });
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.pfx') && !file.name.endsWith('.p12')) {
        toast.error('Selecione um arquivo .pfx ou .p12');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.alias || !uploadForm.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setUploading(true);

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadForm.file!);
      });

      const response = await certificatesApi.upload({
        alias: uploadForm.alias,
        description: uploadForm.description,
        certificate: base64,
        password: uploadForm.password,
      });

      if (response.success) {
        toast.success('Certificado enviado com sucesso!');
        setUploadDialogOpen(false);
        setUploadForm({ alias: '', description: '', password: '', file: null });
        loadData();
      } else {
        toast.error(response.error?.message || 'Erro ao enviar certificado');
      }
    } catch (error: any) {
      toast.error(error.error?.message || 'Erro ao enviar certificado');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string, alias: string) => {
    if (!window.confirm(`Deseja remover o certificado "${alias}"?`)) return;

    try {
      const response = await certificatesApi.delete(id);
      if (response.success) {
        toast.success('Certificado removido');
        loadData();
      } else {
        toast.error(response.error?.message || 'Erro ao remover');
      }
    } catch (error: any) {
      toast.error('Erro ao remover certificado');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'revoked': return 'default';
      default: return 'warning';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'expired': return 'Expirado';
      case 'revoked': return 'Revogado';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
        Integração Sicoob
      </Typography>

      {/* Banner da Empresa Ativa */}
      <Alert
        severity="info"
        icon={<Business />}
        sx={{
          mb: 3,
          backgroundColor: alpha(theme.palette.info.main, 0.08),
          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          '& .MuiAlert-icon': { color: theme.palette.info.main },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          Configurando integração para: {company?.tradingName || company?.name || 'Empresa não identificada'}
        </AlertTitle>
        <Typography variant="body2" color="text.secondary">
          CNPJ: {company?.document ? `${company.document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}` : 'Não informado'}
          {' '} | {' '}
          As configurações abaixo serão vinculadas exclusivamente a esta empresa.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<Settings sx={{ fontSize: 20 }} />} iconPosition="start" label="Configurações" />
          <Tab icon={<VpnKey sx={{ fontSize: 20 }} />} iconPosition="start" label="Certificados" />
          <Tab icon={<Api sx={{ fontSize: 20 }} />} iconPosition="start" label="APIs & Webhooks" />
        </Tabs>

        {/* Tab 0: Configurações */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Dados da Conta */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalance color="primary" />
                      Dados da Conta Sicoob
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ambiente</InputLabel>
                          <Select
                            value={config.environment}
                            label="Ambiente"
                            onChange={(e) => setConfig(prev => ({ ...prev, environment: e.target.value as 'sandbox' | 'production' }))}
                          >
                            <MenuItem value="sandbox">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label="Sandbox" color="info" size="small" />
                                <Typography variant="body2">Ambiente de testes</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="production">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label="Produção" color="success" size="small" />
                                <Typography variant="body2">Ambiente real</Typography>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          label="Cooperativa"
                          fullWidth
                          size="small"
                          required
                          value={config.cooperativa}
                          onChange={(e) => setConfig(prev => ({ ...prev, cooperativa: e.target.value }))}
                          placeholder="Ex: 3113"
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          label="Conta Corrente"
                          fullWidth
                          size="small"
                          required
                          value={config.contaCorrente}
                          onChange={(e) => setConfig(prev => ({ ...prev, contaCorrente: e.target.value }))}
                          placeholder="Ex: 20.854-0"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Conta Poupança"
                          fullWidth
                          size="small"
                          value={config.contaPoupanca}
                          onChange={(e) => setConfig(prev => ({ ...prev, contaPoupanca: e.target.value }))}
                          placeholder="Opcional"
                          helperText="Deixe em branco se não utilizar"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Número do Cliente (Boletos)"
                          fullWidth
                          size="small"
                          value={config.numeroCliente}
                          onChange={(e) => setConfig(prev => ({ ...prev, numeroCliente: e.target.value }))}
                          placeholder="Ex: 123456"
                          helperText="Obrigatório para emissão de boletos. Consulte seu contrato Sicoob."
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Descrição do Aplicativo"
                          fullWidth
                          size="small"
                          value={config.descricaoApp}
                          onChange={(e) => setConfig(prev => ({ ...prev, descricaoApp: e.target.value }))}
                          placeholder="Ex: NOTIFICAÇÕES PIX"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Credenciais */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VpnKey color="primary" />
                      Credenciais do Aplicativo
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Obtenha estas credenciais no <strong>Portal Developers Sicoob</strong> após cadastrar seu aplicativo.
                      </Typography>
                    </Alert>

                    <TextField
                      label="Client ID"
                      fullWidth
                      size="small"
                      required
                      type={showClientId ? 'text' : 'password'}
                      value={config.clientId}
                      onChange={(e) => setConfig(prev => ({ ...prev, clientId: e.target.value }))}
                      placeholder="Ex: 9ceddf0b-4267-412c-98a1-03040823d333"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowClientId(!showClientId)} edge="end" size="small">
                              {showClientId ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                            <IconButton onClick={() => copyToClipboard(config.clientId)} edge="end" size="small">
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Alert severity="info" icon={<Info />}>
                      <Typography variant="body2">
                        <strong>Secret ID:</strong> Não é necessário para utilizar as APIs do Sicoob.
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {/* URLs */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Api color="primary" />
                      URLs de Integração
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="URL de Autenticação"
                          fullWidth
                          size="small"
                          value={config.authUrl}
                          onChange={(e) => setConfig(prev => ({ ...prev, authUrl: e.target.value }))}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => copyToClipboard(config.authUrl)} edge="end" size="small">
                                  <ContentCopy />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="URL API PIX"
                          fullWidth
                          size="small"
                          value={config.apiPixUrl}
                          onChange={(e) => setConfig(prev => ({ ...prev, apiPixUrl: e.target.value }))}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => copyToClipboard(config.apiPixUrl)} edge="end" size="small">
                                  <ContentCopy />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Botões de Ação */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={testingConnection ? <CircularProgress size={16} /> : <LinkIcon />}
                    onClick={handleTestConnection}
                    disabled={!config.clientId || testingConnection}
                  >
                    Testar Conexão
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    onClick={handleSaveConfig}
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : 'Salvar Configurações'}
                  </Button>
                </Box>

                {connectionResult && (
                  <Alert
                    severity={connectionResult.success ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                    onClose={() => setConnectionResult(null)}
                  >
                    {connectionResult.message}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 1: Certificados */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 3 }}>
            {/* Status do Certificado */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={8}>
                {status?.hasCertificate ? (
                  <Alert severity="success">
                    <AlertTitle>Certificado Ativo</AlertTitle>
                    <strong>{status.activeCertificate?.alias}</strong> - Válido até:{' '}
                    {status.activeCertificate?.expiresAt
                      ? format(new Date(status.activeCertificate.expiresAt), 'dd/MM/yyyy', { locale: ptBR })
                      : '-'}
                    {status.activeCertificate && status.activeCertificate.daysUntilExpiry <= 30 && (
                      <Chip
                        icon={<Warning />}
                        label={`Expira em ${status.activeCertificate.daysUntilExpiry} dias`}
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    <AlertTitle>Certificado Não Configurado</AlertTitle>
                    Faça upload de um certificado digital A1 (.pfx) para ativar a integração.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    onClick={() => setUploadDialogOpen(true)}
                    fullWidth
                  >
                    Enviar Certificado
                  </Button>
                  <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
                    Atualizar
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Lista de Certificados */}
            {certificates.length === 0 ? (
              <Alert severity="info">
                Nenhum certificado cadastrado. Clique em "Enviar Certificado" para adicionar.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Titular</TableCell>
                      <TableCell>Validade</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Uso</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certificates.map(cert => (
                      <TableRow key={cert.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{cert.alias}</Typography>
                          {cert.description && (
                            <Typography variant="caption" color="text.secondary">{cert.description}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={cert.subject}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{cert.subject}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(cert.expiresAt), 'dd/MM/yyyy', { locale: ptBR })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={getStatusLabel(cert.status)} color={getStatusColor(cert.status)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{cert.usageCount}x</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remover">
                            <IconButton color="error" size="small" onClick={() => handleDelete(cert.id, cert.alias)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: APIs & Webhooks */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 3 }}>
            {/* Card de Webhook */}
            <Card
              variant="outlined"
              sx={{
                mb: 3,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }
              }}
              onClick={() => navigate('/settings/webhook-logs')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Webhook color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Webhooks Sicoob
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configure a URL de webhook, visualize logs e monitore notificações recebidas
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={(e) => { e.stopPropagation(); navigate('/settings/webhook-logs'); }}
                  >
                    Acessar
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* APIs Habilitadas */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              APIs Habilitadas
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      PIX Recebimentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <code style={{ fontSize: '0.75rem' }}>https://api.sicoob.com.br/pix/api/v2</code>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {[
                        'pix.read', 'pix.write', 'cob.read', 'cob.write',
                        'cobv.read', 'cobv.write', 'webhook.read', 'webhook.write'
                      ].map(scope => (
                        <Chip key={scope} label={scope} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Conta Corrente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <code style={{ fontSize: '0.75rem' }}>https://api.sicoob.com.br/conta-corrente/v2</code>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {[
                        'cco_extrato', 'cco_saldo', 'movimentacoes.read'
                      ].map(scope => (
                        <Chip key={scope} label={scope} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Links Úteis
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="text" size="small" href="https://developers.sicoob.com.br" target="_blank">
                Portal Developers Sicoob
              </Button>
              <Button variant="text" size="small" href="https://developers.sicoob.com.br/portal/documentacao" target="_blank">
                Documentação das APIs
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Dialog de Upload */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Certificado Digital</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              O certificado deve ser no formato .pfx ou .p12 (ICP-Brasil A1)
            </Alert>

            <TextField
              label="Nome do Certificado"
              fullWidth
              required
              value={uploadForm.alias}
              onChange={e => setUploadForm(prev => ({ ...prev, alias: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="Ex: Certificado Principal"
            />

            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={uploadForm.description}
              onChange={e => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="Descrição opcional..."
            />

            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept=".pfx,.p12"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                sx={{ py: 2 }}
              >
                {uploadForm.file ? uploadForm.file.name : 'Selecionar arquivo .pfx'}
              </Button>
            </Box>

            <TextField
              label="Senha do Certificado"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              value={uploadForm.password}
              onChange={e => setUploadForm(prev => ({ ...prev, password: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {uploading && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadForm.file || !uploadForm.alias || !uploadForm.password}
          >
            {uploading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SicoobSettingsPage;
